/**
 * Report-view configuration for DocTypeListPageShell (store + URL `group_by` / `agg_*`).
 *
 * Extracted from DocTypeListPageShell (file-size split, behaviour unchanged).
 */

import { useCallback, useMemo } from "react";
import type { SetURLSearchParams } from "react-router-dom";
import { usePreferencesStore, type ReportConfig } from "@/lib/store/preferencesStore";
import type { AggregationType } from "@/lib/erp";

export function useShellReportConfig({
  doctype,
  searchParams,
  setSearchParams,
}: {
  doctype: string;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}) {
  // Report configuration state (from store + URL)
  const storedReportConfig = usePreferencesStore(
    useCallback((s) => s.docTypeReportConfig[doctype], [doctype])
  );

  // Parse report config from URL (group_by=field&agg_fieldname=sum)
  const urlGroupBy = searchParams.get('group_by');
  const urlAggregations = useMemo(() => {
    const aggs: Record<string, AggregationType> = {};
    searchParams.forEach((value, key) => {
      if (key.startsWith('agg_') && ['sum', 'avg', 'count'].includes(value)) {
        aggs[key.slice(4)] = value as AggregationType;
      }
    });
    return aggs;
  }, [searchParams]);

  const reportConfig: ReportConfig = useMemo(() => ({
    groupByField: urlGroupBy ?? storedReportConfig?.groupByField,
    aggregations: Object.keys(urlAggregations).length > 0
      ? urlAggregations
      : (storedReportConfig?.aggregations ?? {}),
    visibleColumns: storedReportConfig?.visibleColumns ?? [],
  }), [urlGroupBy, urlAggregations, storedReportConfig]);

  const setReportConfig = useCallback((config: Partial<ReportConfig>) => {
    // Update store
    usePreferencesStore.getState().setDocTypeReportConfig(doctype, config);

    // Update URL
    const newParams = new URLSearchParams(searchParams);

    if (config.groupByField !== undefined) {
      if (config.groupByField) {
        newParams.set('group_by', config.groupByField);
      } else {
        newParams.delete('group_by');
      }
    }

    if (config.aggregations !== undefined) {
      // Clear existing agg_ params
      const keysToDelete: string[] = [];
      newParams.forEach((_value, key) => {
        if (key.startsWith('agg_')) keysToDelete.push(key);
      });
      keysToDelete.forEach((key) => newParams.delete(key));

      // Add new agg_ params
      Object.entries(config.aggregations).forEach(([field, aggType]) => {
        newParams.set(`agg_${field}`, aggType);
      });
    }

    setSearchParams(newParams, { replace: true });
  }, [doctype, searchParams, setSearchParams]);

  return { reportConfig, setReportConfig };
}
